import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import PendingIcon from '@mui/icons-material/Pending';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { Organization } from '../components/OrganizationSelector';
import PlanLimitDialog from '../components/PlanLimitDialog';

// NOTE: All non-auth endpoints require /api/v1 prefix
import { API_URL } from '../constants/urls';

interface Member {
  id: string;
  user_name: string | null;
  user_email: string | null;
  role: string;
  invitation_sent_at: string | null;
  invitation_last_sent_at: string | null;
  invitation_accepted_at: string | null;
  is_pending: boolean;
}

interface ManageTeamProps {
  organization: Organization | null;
  isReadOnly?: boolean;
  userRole?: string;
}

export default function ManageTeam({
  organization,
  isReadOnly = false,
  userRole = 'viewer',
}: ManageTeamProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Plan limit dialog state
  const [planLimitDialog, setPlanLimitDialog] = useState<{
    open: boolean;
    limitType: 'user' | 'editor' | 'proposition' | 'segment';
    currentCount?: number;
    limit?: number;
  }>({ open: false, limitType: 'user' });

  const [organizationDetails, setOrganizationDetails] = useState<any>(null);

  const fetchMembers = () => {
    if (!organization) return;
    setLoading(true);
    axios
      .get(`${API_URL}/organizations/${organization.id}/members`)
      .then((res) => {
        setMembers(res.data);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (organization) {
      fetchMembers();
      // Fetch organization details to get plan_type
      axios
        .get(`${API_URL}/organizations/${organization.id}`)
        .then((res) => {
          setOrganizationDetails(res.data);
        })
        .catch((_error) => {
          // console.error removed
        });
    }
  }, [organization]);

  const handleInvite = () => {
    if (!organization) return;

    // Check editor seats if inviting an editor or admin
    if (
      (inviteRole === 'editor' || inviteRole === 'admin') &&
      organizationDetails
    ) {
      const currentEditors = members.filter(
        (m) => m.role === 'editor' || m.role === 'admin',
      ).length;
      const maxEditors = organizationDetails.max_editors;

      if (maxEditors && currentEditors >= maxEditors) {
        setSnackbar({
          open: true,
          message: `You've reached the maximum number of editors (${maxEditors}) for your plan. Upgrade your subscription or change an existing member to viewer role.`,
          severity: 'error',
        });
        return;
      }
    }

    setInviting(true);
    axios
      .post(`${API_URL}/organizations/${organization.id}/members`, {
        email: inviteEmail,
        role: inviteRole,
      })
      .then(() => {
        setInviteOpen(false);
        setInviteEmail('');
        setInviteRole('editor');
        fetchMembers();
        setSnackbar({
          open: true,
          message: 'Invitation sent successfully!',
          severity: 'success',
        });
      })
      .catch((error) => {
        const errorDetail = error.response?.data?.detail;

        // Check if it's a plan limit error
        if (
          typeof errorDetail === 'object' &&
          errorDetail?.error === 'user_limit_exceeded'
        ) {
          setPlanLimitDialog({
            open: true,
            limitType: 'user',
            currentCount: errorDetail.current_count,
            limit: errorDetail.limit,
          });
        } else if (
          typeof errorDetail === 'object' &&
          errorDetail?.error === 'editor_limit_exceeded'
        ) {
          setPlanLimitDialog({
            open: true,
            limitType: 'editor',
            currentCount: errorDetail.current_editor_count,
            limit: errorDetail.editor_limit,
          });
        } else {
          // Show regular error message
          const errorMessage =
            typeof errorDetail === 'string'
              ? errorDetail
              : errorDetail?.message || 'Failed to send invitation';

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
      })
      .finally(() => setInviting(false));
  };

  const handleRemove = (memberId: string) => {
    if (!organization) return;
    axios
      .delete(`${API_URL}/organizations/${organization.id}/members/${memberId}`)
      .then(fetchMembers)
      .catch((error) => {
        const errorDetail = error.response?.data?.detail;
        const errorMessage =
          typeof errorDetail === 'string'
            ? errorDetail
            : errorDetail?.message || 'Failed to remove member';

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      });
  };

  const handleRoleChange = (memberId: string, newRole: string) => {
    if (!organization) return;
    axios
      .put(`${API_URL}/organizations/${organization.id}/members/${memberId}`, {
        role: newRole,
      })
      .then(fetchMembers)
      .catch((error) => {
        const errorDetail = error.response?.data?.detail;

        // Check if it's a plan limit error
        if (
          typeof errorDetail === 'object' &&
          errorDetail?.error === 'editor_limit_exceeded'
        ) {
          setPlanLimitDialog({
            open: true,
            limitType: 'editor',
            currentCount: errorDetail.current_editor_count,
            limit: errorDetail.editor_limit,
          });
        } else if (
          typeof errorDetail === 'object' &&
          errorDetail?.error === 'admin_required'
        ) {
          // Special case for admin requirement
          setSnackbar({
            open: true,
            message:
              errorDetail.message ||
              'Free plan requires at least one admin user',
            severity: 'error',
          });
        } else {
          const errorMessage =
            typeof errorDetail === 'string'
              ? errorDetail
              : errorDetail?.message || 'Failed to update role';

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }
        // Refresh to show original role
        fetchMembers();
      });
  };

  const handleResendInvitation = (memberId: string) => {
    if (!organization) return;
    const member = members.find((m) => m.id === memberId);
    const isFirstInvite = member && !member.invitation_last_sent_at;

    setResending(memberId);
    axios
      .post(
        `${API_URL}/organizations/${organization.id}/members/${memberId}/resend-invitation`,
      )
      .then(() => {
        fetchMembers();
        setSnackbar({
          open: true,
          message: isFirstInvite
            ? 'Invitation sent successfully!'
            : 'Invitation resent successfully!',
          severity: 'success',
        });
      })
      .catch((error) => {
        const errorDetail = error.response?.data?.detail;
        const errorMessage =
          typeof errorDetail === 'string'
            ? errorDetail
            : errorDetail?.message || 'Failed to send invitation';

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      })
      .finally(() => setResending(null));
  };

  if (!organization) {
    return (
      <Box>
        <Typography variant="h6">
          Select an organization to manage team members.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" className="mb-4 text-lg sm:text-xl">
        Team Members
      </Typography>
      {isReadOnly && (
        <Alert severity="info" className="mb-4 text-sm">
          You have {userRole} access.{' '}
          {userRole === 'editor'
            ? 'You can view team members but cannot make changes.'
            : 'You have read-only access to team information.'}
        </Alert>
      )}
      {!isReadOnly && (
        <Button
          variant="contained"
          onClick={() => setInviteOpen(true)}
          className="mb-4 w-full sm:w-auto"
          size="medium"
        >
          Invite Member
        </Button>
      )}

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>Loading...</TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>No members found.</TableCell>
                </TableRow>
              ) : (
                members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{m.user_name || m.user_email}</TableCell>
                    <TableCell>{m.user_email}</TableCell>
                    <TableCell>
                      <Select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.id, e.target.value)}
                        size="small"
                        disabled={m.is_pending || isReadOnly}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="editor">Editor</MenuItem>
                        <MenuItem value="viewer">Viewer</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {m.is_pending ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PendingIcon color="warning" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Pending
                            {m.invitation_last_sent_at ? (
                              <>
                                {' '}
                                (
                                {formatDistanceToNow(
                                  new Date(m.invitation_last_sent_at),
                                  { addSuffix: true },
                                )}
                                )
                              </>
                            ) : (
                              <> (never invited)</>
                            )}
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircleIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            Active
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        {m.is_pending && !isReadOnly && (
                          <IconButton
                            color="primary"
                            onClick={() => handleResendInvitation(m.id)}
                            disabled={resending === m.id}
                            title={
                              m.invitation_last_sent_at
                                ? 'Resend invitation'
                                : 'Send invitation'
                            }
                          >
                            <SendIcon />
                          </IconButton>
                        )}
                        {!isReadOnly && (
                          <IconButton
                            color="error"
                            onClick={() => handleRemove(m.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <Card>
            <CardContent>
              <Typography>Loading...</Typography>
            </CardContent>
          </Card>
        ) : members.length === 0 ? (
          <Card>
            <CardContent>
              <Typography>No members found.</Typography>
            </CardContent>
          </Card>
        ) : (
          members.map((m) => (
            <Card key={m.id} className="shadow-sm">
              <CardContent className="p-4">
                {/* Name/Email and Role on same row */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1 pr-3">
                    <Typography variant="subtitle1" className="font-medium">
                      {m.user_name || m.user_email}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="text-sm"
                    >
                      {m.user_email}
                    </Typography>
                  </div>
                  <Select
                    value={m.role}
                    onChange={(e) => handleRoleChange(m.id, e.target.value)}
                    size="small"
                    disabled={m.is_pending || isReadOnly}
                    className="min-w-[100px]"
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                </div>

                {/* Status and Actions on same row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {m.is_pending ? (
                      <>
                        <PendingIcon color="warning" fontSize="small" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="text-sm"
                        >
                          Pending
                          {m.invitation_last_sent_at && (
                            <span className="text-xs">
                              {' '}
                              (
                              {formatDistanceToNow(
                                new Date(m.invitation_last_sent_at),
                                { addSuffix: true },
                              )}
                              )
                            </span>
                          )}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="text-sm"
                        >
                          Active
                        </Typography>
                      </>
                    )}
                  </div>

                  {!isReadOnly && (
                    <div className="flex items-center gap-1">
                      {m.is_pending && (
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleResendInvitation(m.id)}
                          disabled={resending === m.id}
                          title={
                            m.invitation_last_sent_at
                              ? 'Resend invitation'
                              : 'Send invitation'
                          }
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemove(m.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            fullWidth
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="editor">Editor</MenuItem>
            <MenuItem value="viewer">Viewer</MenuItem>
          </Select>
          {organizationDetails?.max_editors && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 2, display: 'block' }}
            >
              Editor seats used:{' '}
              {
                members.filter((m) => m.role === 'editor' || m.role === 'admin')
                  .length
              }{' '}
              / {organizationDetails.max_editors}
              {members.filter((m) => m.role === 'editor' || m.role === 'admin')
                .length >= organizationDetails.max_editors && (
                <span style={{ color: 'warning.main', display: 'block' }}>
                  No editor seats available. Invite as viewer or upgrade your
                  plan.
                </span>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            disabled={inviting || !inviteEmail}
            variant="contained"
          >
            Invite
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PlanLimitDialog
        open={planLimitDialog.open}
        onClose={() => setPlanLimitDialog({ ...planLimitDialog, open: false })}
        limitType={planLimitDialog.limitType}
        currentPlan={organizationDetails?.plan_type || 'free'}
        currentCount={planLimitDialog.currentCount}
        limit={planLimitDialog.limit}
      />
    </Box>
  );
}
